//
// # Message Types
//

// NPM Imports
import typia from "typia";

// Local Imports
import { type Uuid } from "./uuid";
import { Validator } from "./validate";

// # Enumerated Message Kinds
// Each serves as a type-tag for the message.
export enum Kind {
  EditorUp = "editor-up",
  New = "new",
  MainContent = "main-content",
  UpdateMainContentRequest = "update-main-content-request",
  UpdateMainContentResponse = "update-main-content-response",
  RefreshMainContent = "refresh-main-content",
  RefreshMainContentResponse = "refresh-main-content-response",
  GetMainContentRequest = "request-main-content",
  ContentRequest = "content-request",
  ContentResponse = "content-response",
  GetPdkRequest = "get-pdk-request",
  GetPdkResponse = "get-pdk-response",
  GetStackRequest = "get-stack-request",
  GetStackResponse = "get-stack-response",
  PromptRequest = "prompt-request",
  PromptResponse = "prompt-response",
  UpdateSettings = "update-settings",
  UIChange = "ui-change",
  EditInstanceParameters = "edit-instance-parameters",
  AddInstanceAtPosition = "add-instance-at-position",
  AddPinAtPosition = "add-pin-at-position",
  RenameInstance = "rename-instance",
  RenamePin = "rename-pin",

  GenerateFromSourceRequest = "generate-from-source-request",
  OpenInstanceTarget = "open-instance-target",
  ViewInDepot = "view-in-depot",
  ProcessingState = "processing-state",
  ToggleParasitics = "toggle-parasitics",
  ParasiticsStatusUpdate = "parasitics-status-update",
  GetParasiticsSummary = "get-parasitics-summary",
  ParasiticsSummaryResponse = "parasitics-summary-response",
  CaptureCanvasRequest = "capture-canvas-request",
  CaptureCanvasResponse = "capture-canvas-response",
  Log = "log",
  Warning = "warning",
  Error = "error",
}

export type Call<KindType extends Kind> = {
  kind: KindType;
  uuid: Uuid;
  data: unknown;
};

export type Return<KindType extends Kind> = {
  kind: KindType;
  uuid: Uuid;
  data: unknown;
};

export type GetPdkRequest = Call<Kind.GetPdkRequest>;
export type GetPdkResponse = Return<Kind.GetPdkResponse>;
export type GetStackRequest = Call<Kind.GetStackRequest>;
export type GetStackResponse = Return<Kind.GetStackResponse>;

export type ContentType = "schematic" | "symbol" | "layout";
export const ContentType = {
  Schematic: "schematic",
  Symbol: "symbol",
  Layout: "layout",
} as const;

// Type for distinguishing between loading new content vs refreshing existing content
export type LoadKind = "new" | "refresh";
export const LoadKind = {
  new: "new" as const,
  refresh: "refresh" as const,
};

// # Editor Up
// The editor has started up and is ready to receive messages.
export type EditorUp = {
  kind: Kind.EditorUp;
  contentType?: ContentType;
};

// # Logging Messages
// With varying severity levels.
export type Logging = {
  kind: Kind.Error | Kind.Warning | Kind.Log;
  body: unknown; // This ones actually unknown! Anything JSON-able.
};

// # Message with the editor's main content
export type MainContent = {
  kind: Kind.MainContent;
  content: unknown; // Really `Content`
};

// # Request that the editor load a new set of main content
export type UpdateMainContentRequest = {
  kind: Kind.UpdateMainContentRequest;
  uuid: Uuid; // Request ID
  content: unknown; // Really `Content`
};

// Response indicating that content is done loading
export type UpdateMainContentResponse = {
  kind: Kind.UpdateMainContentResponse;
  uuid: Uuid; // Request ID
} & ({ success: true } | { success: false; error: unknown });

// # Request that the editor refresh (not replace) main content
export type RefreshMainContentRequest = {
  kind: Kind.RefreshMainContent;
  uuid: Uuid; // Request ID
  content: unknown; // Really `Content`
  savedIdents?: unknown[]; // Sub-designs whose change triggered this refresh, for targeted cache invalidation
};

// Response indicating that content refresh is done
export type RefreshMainContentResponse = {
  kind: Kind.RefreshMainContentResponse;
  uuid: Uuid; // Request ID
} & ({ success: true } | { success: false; error: unknown });

// Request the content of the current schematic or symbol
export type GetMainContentRequest = {
  kind: Kind.GetMainContentRequest;
  uuid: Uuid; // Request ID
};

export type ContentRequest = {
  kind: Kind.ContentRequest;
  uuid: Uuid; // Request ID
  ident: unknown; // Really `Ident` being requested
};

export type ContentResponse = {
  kind: Kind.ContentResponse;
  uuid: Uuid; // Request ID
} & ({ success: true; content: unknown } | { success: false; error: string });

// # New
// Create a new schematic or symbol.
export type New = {
  kind: Kind.New;
  contentKind: string; // Really a `ContentType`
};

// # Editor Settings
export interface Settings {
  gridSnap?: number;
  gridMinor?: number;
  gridMultiple?: number;
  theme?: "light" | "dark" | "high-contrast" | "high-contrast-light";
}

// # Update Editor Settings
export type UpdateSettings = {
  kind: Kind.UpdateSettings;
  settings: Settings;
};

export interface PromptRequest {
  kind: Kind.PromptRequest;
  uuid: Uuid; // Request ID
  prompt: string;
}

export interface PromptResponse {
  kind: Kind.PromptResponse;
  uuid: Uuid; // Request ID
}

// UI Change - when Editor makes changes via UI interaction
export type UIChange = {
  kind: Kind.UIChange;
  change: unknown; // Schema-based change data
};

// Edit Instance Parameters - when user requests to edit instance parameters from context menu
export type EditInstanceParameters = {
  kind: Kind.EditInstanceParameters;
  instanceId: string;
};

// Add Instance At Position - when user requests to add an instance at a specific position from context menu
export type AddInstanceAtPosition = {
  kind: Kind.AddInstanceAtPosition;
  position: { x: number; y: number };
};

// Add Pin At Position - when user requests to add a pin at a specific position from context menu
export type AddPinAtPosition = {
  kind: Kind.AddPinAtPosition;
  position: { x: number; y: number };
};

// Rename Instance - when user requests to rename an instance from context menu
export type RenameInstance = {
  kind: Kind.RenameInstance;
  instanceId: string;
};

// Rename Pin - when user requests to rename a pin from context menu
export type RenamePin = {
  kind: Kind.RenamePin;
  pinId: number;
  currentName: string;
};

// Generate From Source Request - when user requests to generate a layout from a schematic ("Generate From Source") via context menu
export type GenerateFromSourceRequest = {
  kind: Kind.GenerateFromSourceRequest;
};

// Open Instance Target - when user requests to open an instance's target design in a new tab
export type OpenInstanceTarget = {
  kind: Kind.OpenInstanceTarget;
  target: unknown; // Really schema.Ident - the target design to open
};

// View In Depot - when user requests to reveal an instance's target in the Depot tree
export type ViewInDepot = {
  kind: Kind.ViewInDepot;
  target: unknown; // Really schema.Ident - the target design to reveal
};

// Processing State - when Platform needs to update Editor's processing state
export type ProcessingState = {
  kind: Kind.ProcessingState;
  processing: boolean;
};

// Toggle Parasitics - when user toggles parasitic extraction mode
export type ToggleParasitics = {
  kind: Kind.ToggleParasitics;
};

// Parasitics Status Update - when parasitics mode status changes
export type ParasiticsStatusUpdate = {
  kind: Kind.ParasiticsStatusUpdate;
  enabled: boolean;
  segmentCount?: number;
  viaCount?: number;
};

// Get Parasitics Summary - request summary of extraction results
export type GetParasiticsSummary = {
  kind: Kind.GetParasiticsSummary;
};

// Parasitics Summary Response - detailed summary of extraction results
export type ParasiticsSummaryResponse = {
  kind: Kind.ParasiticsSummaryResponse;
} & (
  | {
      success: true;
      summary: {
        totalSegments: number;
        totalVias: number;
        totalCouplingPairs: number;
        totalWireResistance: number;
        totalViaResistance: number;
        totalGroundCapacitance: number;
        totalCouplingCapacitance: number;
        netSummaries: Array<{
          name: string;
          wireResistance: number;
          viaResistance: number;
          groundCapacitance: number;
          couplingCapacitance: number;
          segmentCount: number;
          viaCount: number;
        }>;
        topCouplingPairs: Array<{
          netA: string;
          netB: string;
          capacitance: number;
        }>;
      };
    }
  | { success: false; error: string }
);

// Capture Canvas Request - request a screenshot of the current canvas
export type CaptureCanvasRequest = {
  kind: Kind.CaptureCanvasRequest;
  uuid: Uuid;
};

// Capture Canvas Response - returns base64 encoded PNG image
export type CaptureCanvasResponse = {
  kind: Kind.CaptureCanvasResponse;
  uuid: Uuid;
} & (
  | { success: true; imageBase64: string }
  | { success: false; error: string }
);

//
// # Messages
// The primary `Message` union types.
//
// Individual message types do not have their own validators.
// Validation is performed at the union level via `ToPlatform.validator` and `ToEditor.validator`.
//

// Messages from the editor to the platform
export type ToPlatform =
  | EditorUp
  | UpdateMainContentResponse
  | RefreshMainContentResponse
  | MainContent
  | ContentRequest
  | ContentResponse
  | PromptRequest
  | UIChange
  | EditInstanceParameters
  | AddInstanceAtPosition
  | AddPinAtPosition
  | RenameInstance
  | RenamePin
  | GenerateFromSourceRequest
  | OpenInstanceTarget
  | ViewInDepot
  | ToggleParasitics
  | GetParasiticsSummary
  | CaptureCanvasResponse
  | Logging
  | GetPdkRequest
  | GetStackRequest;

export const ToPlatform = {
  validator: new Validator(typia.createValidate<ToPlatform>()),
  stringify: typia.json.createStringify<ToPlatform>(),
};

// Messages from the platform to the editor
export type ToEditor =
  | GetMainContentRequest
  | UpdateMainContentRequest
  | RefreshMainContentRequest
  | ContentResponse
  | New
  | UpdateSettings
  | PromptResponse
  | GetPdkResponse
  | GetStackResponse
  | ProcessingState
  | ParasiticsStatusUpdate
  | ParasiticsSummaryResponse
  | CaptureCanvasRequest
  | Logging;

export const ToEditor = {
  validator: new Validator(typia.createValidate<ToEditor>()),
  stringify: typia.json.createStringify<ToEditor>(),
};

export const Message = { Kind };

// Editor-side message handler type
export type Handler = (m: ToEditor) => unknown;
